import type * as FrigadeAssistantPackage from '@frigade/assistant';
import type {EventCallback, EventType, FrigadeAssistantConfig} from '@frigade/assistant';

import {isTestEnv} from 'common/report/util/env';

// The Frigade assistant package is imported dynamically so its bundle is only
// fetched for users who open or prewarm the assistant. `init()` resolves once
// the React root is rendered; the imperative methods (`show`, `hide`, `close`,
// `getMode`, `ask`) are attached to `window.FrigadeAssistant` afterwards from
// the root's effects, so call sites must check that the method exists.
// Docs: https://docs.frigade.com
export type FrigadeAssistantMode =
  | `hidden`
  | `collapsed`
  | `input`
  | `thinking`
  | `streaming-result`
  | `result`
  | `reply-input`
  | `tool-ui`
  | `error`;

// Paired with the `api.frigade.ai` connect-src entries in
// kube/lib/webapp/nginx/csp-config.libsonnet - changing one needs the other.
const FRIGADE_API_URL = `https://api.frigade.ai`;

export interface LoadFrigadeAssistantOptions {
  publicKey: string;
  assistantId: string;
  // Required: the vendor throws "Please provide a userId to initialize Frigade"
  // without it, and it keys the conversation on their side.
  userId: string;
  userProperties?: FrigadeAssistantConfig[`userProperties`];
  preferences?: FrigadeAssistantConfig[`preferences`];
  tools?: FrigadeAssistantConfig[`tools`];
}

// Bounds the package import and `init()`: a request that never settles would
// otherwise hold the cached import or the init queue forever.
const FRIGADE_LOAD_TIMEOUT_MS = 10_000;

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, description: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(`${description} timed out`)), timeoutMs);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        window.clearTimeout(timer);
        reject(error instanceof Error ? error : new Error(String(error)));
      },
    );
  });
}

let _frigadePackagePromise: Promise<typeof FrigadeAssistantPackage> | null = null;

// Failures and timeouts clear the cached promise so the next call retries the
// chunk fetch.
function importFrigadePackage(): Promise<typeof FrigadeAssistantPackage> {
  if (!_frigadePackagePromise) {
    _frigadePackagePromise = withTimeout(import(`@frigade/assistant`), FRIGADE_LOAD_TIMEOUT_MS, `Frigade import`).catch(
      (error: unknown) => {
        _frigadePackagePromise = null;
        throw error;
      },
    );
  }
  return _frigadePackagePromise;
}

// Keyed by publicKey/assistantId/userId so a credential or identity change
// between activations doesn't return the stale promise. Only prewarm consults
// it: `loadFrigadeAssistant` re-initializes on every call.
const _frigadeLoads = new Map<string, Promise<boolean>>();

// `init()` calls for the same page must not overlap: each one replaces the
// previous React root, and the readiness wait compares against the `show`
// closure installed by the root before it. At most one init runs and one waits.
// A call arriving while an init is waiting overwrites its config and shares its
// promise, so both callers get the result of the newest config.
interface PendingInit {
  config: FrigadeAssistantConfig;
  promise: Promise<boolean>;
  resolve: (isReady: boolean) => void;
}

let _pendingInit: PendingInit | null = null;
let _initRunning = false;
// The config of the most recently started init and a counter of started inits,
// so an init that settles after its timeout can tell whether a newer one ran.
let _latestStartedConfig: FrigadeAssistantConfig | null = null;
let _startedInitCount = 0;

// Counts visibility requests made through this module and records the latest
// one, so a late re-init applies a show or hide requested while it ran.
let _visibilityRequestCount = 0;
let _lastRequestedVisible = false;

function recordVisibilityRequest(isVisible: boolean): void {
  _visibilityRequestCount++;
  _lastRequestedVisible = isVisible;
}

function takePendingInit(): PendingInit | null {
  const pending = _pendingInit;
  _pendingInit = null;
  return pending;
}

function scheduleInit(config: FrigadeAssistantConfig): Promise<boolean> {
  if (_pendingInit) {
    _pendingInit.config = config;
    return _pendingInit.promise;
  }
  let resolve: (isReady: boolean) => void = () => {};
  const promise = new Promise<boolean>((resolvePromise) => {
    resolve = resolvePromise;
  });
  _pendingInit = {config, promise, resolve};
  if (!_initRunning) {
    void drainInitQueue();
  }
  return promise;
}

// The queue does not wait for an init that timed out, since one that never
// settles would block every later load. If it does settle after a newer init
// started, its root replaced the newer one with a stale config, so the newest
// config is initialized again. A queued init already does that, so it is left
// alone. The re-initialized root starts hidden (`defaultVisible: false`), so it
// is shown again if the newer root was visible, unless a show/hide/close/ask
// call during the re-init asked for something else: then the latest request
// wins, since a call made during the remount may have reached the old root.
// The late root's effects have not run when its `init()` settles, so
// `getMode()` still reads the newer root.
function reinitIfSettledLate(initPromise: Promise<void>, initCountAtStart: number): void {
  initPromise.then(
    () => {
      if (_startedInitCount !== initCountAtStart && !_pendingInit && _latestStartedConfig) {
        const modeBeforeReinit = getFrigadeMode();
        const wasVisible = modeBeforeReinit !== undefined && modeBeforeReinit !== `hidden`;
        const visibilityRequestCountAtReinit = _visibilityRequestCount;
        void scheduleInit(_latestStartedConfig).then((isReady) => {
          const requestedDuringReinit = _visibilityRequestCount !== visibilityRequestCountAtReinit;
          const shouldBeVisible = requestedDuringReinit ? _lastRequestedVisible : wasVisible;
          if (isReady && shouldBeVisible) {
            showFrigadeAssistant();
          }
        });
      }
    },
    () => undefined,
  );
}

async function drainInitQueue(): Promise<void> {
  _initRunning = true;
  try {
    while (_pendingInit) {
      let frigadePackage: typeof FrigadeAssistantPackage;
      try {
        frigadePackage = await importFrigadePackage();
      } catch {
        takePendingInit()?.resolve(false);
        continue;
      }
      // Taken only after the import resolves, so a call made while the package
      // was still downloading supersedes the config that triggered the import.
      const pending = takePendingInit();
      if (!pending) {
        break;
      }
      const previousShow = window.FrigadeAssistant?.show;
      _latestStartedConfig = pending.config;
      const initCountAtStart = ++_startedInitCount;
      const initPromise = frigadePackage.init(pending.config);
      try {
        await withTimeout(initPromise, FRIGADE_LOAD_TIMEOUT_MS, `Frigade init`);
        pending.resolve(await whenImperativeApiReady(previousShow));
      } catch {
        pending.resolve(false);
        reinitIfSettledLate(initPromise, initCountAtStart);
      }
    }
  } finally {
    _initRunning = false;
  }
}

function identityCacheKey(publicKey: string, assistantId: string, userId: string): string {
  return `${publicKey}:${assistantId}:${userId}`;
}

// Start fetching the package and initialize with a bare identity, without
// showing the assistant. Safe to call repeatedly: once any config has been sent
// for this identity - this bare one or a full one carrying `userProperties` -
// later prewarms are no-ops, so they can never overwrite the properties with
// nothing.
export function prewarmFrigadeAssistant(publicKey: string, assistantId: string, userId: string): void {
  if (!publicKey || !assistantId || !userId) {
    return;
  }
  if (_frigadeLoads.has(identityCacheKey(publicKey, assistantId, userId))) {
    return;
  }
  void loadFrigadeAssistant({publicKey, assistantId, userId});
}

export function showFrigadeAssistant(): void {
  recordVisibilityRequest(true);
  window.FrigadeAssistant?.show?.();
}

// Frigade drops an in-flight response when hidden mid-answer rather than
// saving it - callers should consult `getFrigadeMode()` before hiding while
// the assistant is thinking/streaming.
export function hideFrigadeAssistant(): void {
  recordVisibilityRequest(false);
  window.FrigadeAssistant?.hide?.();
}

/**
 * @lintignore (knip) TODO(GRO-252): temporarily unreferenced — the visibility lifecycle work discards the conversation when the assistant is dismissed
 */
export function closeFrigadeConversation(): void {
  recordVisibilityRequest(false);
  window.FrigadeAssistant?.close?.();
}

export function getFrigadeMode(): FrigadeAssistantMode | undefined {
  // `widget` is the deprecated name the package reports for `collapsed`.
  const mode = window.FrigadeAssistant?.getMode?.();
  return mode === `widget` ? `collapsed` : mode;
}

/**
 * `ask()` works on a hidden assistant and brings it back on screen by itself.
 *
 * @lintignore (knip) TODO(GRO-250): temporarily unreferenced — consumed by the entry points that seed the assistant with a question
 */
export function askFrigadeAssistant(question: string): void {
  recordVisibilityRequest(true);
  window.FrigadeAssistant?.ask?.({question});
}

// Frigade's `on()` does not dedupe handlers, so register once per page.
let _listenerRegistration: Promise<void> | null = null;

/**
 * Subscribes the handlers through the package's `on()`, once per page. Later
 * calls return the first call's promise; a failed package import clears it so
 * the next call retries.
 *
 * @lintignore (knip) TODO(GRO-254): temporarily unreferenced — the conversation-tracking work subscribes to the assistant's events
 */
export function registerFrigadeEventHandlers(handlers: Partial<Record<EventType, EventCallback>>): Promise<void> {
  if (!_listenerRegistration) {
    _listenerRegistration = importFrigadePackage().then(
      ({on}) => {
        for (const [event, handler] of Object.entries(handlers) as Array<[EventType, EventCallback]>) {
          on(event, handler);
        }
      },
      (error: unknown) => {
        _listenerRegistration = null;
        throw error;
      },
    );
  }
  return _listenerRegistration;
}

// `init()` resolves before the root's effects run, and those effects attach
// `show`/`hide`/`close`/`getMode`/`ask`. Every call site uses optional
// chaining, so resolving early would silently no-op them.
//
// Every `init()` after the first mounts a new React root whose effects
// overwrite the methods with fresh closures. The effects have no cleanup, so
// between `init()` and the new mount the OLD closures stay attached and
// dispatch into a dead tree. `previousShow` is the `show` the root this call
// replaces had installed; the API is ready only once something else is
// attached in its place.
const FRIGADE_API_READY_TIMEOUT_MS = 10_000;
const FRIGADE_API_POLL_MS = 50;

function whenImperativeApiReady(previousShow: (() => void) | undefined): Promise<boolean> {
  const isAttached = () => {
    const show = window.FrigadeAssistant?.show;
    return show !== undefined && show !== previousShow;
  };
  if (isAttached()) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const deadline = Date.now() + FRIGADE_API_READY_TIMEOUT_MS;
    const timer = window.setInterval(() => {
      if (isAttached()) {
        window.clearInterval(timer);
        resolve(true);
      } else if (Date.now() >= deadline) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, FRIGADE_API_POLL_MS);
  });
}

export function loadFrigadeAssistant(options: LoadFrigadeAssistantOptions): Promise<boolean> {
  const {publicKey, assistantId, userId, userProperties, preferences, tools} = options;
  // Empty credentials mean the backend couldn't resolve this environment's
  // Frigade key or assistant yet - skip the load so the page degrades to
  // "no assistant" instead of loading an unverified widget.
  if (isTestEnv() || !publicKey || !assistantId || !userId) {
    return Promise.resolve(false);
  }
  const cacheKey = identityCacheKey(publicKey, assistantId, userId);
  // `init()` runs on every call, not just the first per identity. The package
  // exposes no method for updating a user after init: re-invoking it is the
  // contract for refreshed `userProperties` (it re-initializes in place, and a
  // conversation under five minutes old survives via sessionStorage).
  // `defaultVisible: false` because the Help menu drives visibility - the
  // assistant must never paint its own launcher button.
  const config: FrigadeAssistantConfig = {
    apiKey: publicKey,
    apiUrl: FRIGADE_API_URL,
    assistantId,
    userId,
    userProperties,
    preferences,
    tools,
    defaultVisible: false,
  };
  const ready: Promise<boolean> = scheduleInit(config).then((isReady) => {
    // Dropping the entry lets the next prewarm for this identity retry.
    // Only the newest load owns the entry: a later call may have replaced it.
    if (!isReady && _frigadeLoads.get(cacheKey) === ready) {
      _frigadeLoads.delete(cacheKey);
    }
    return isReady;
  });
  _frigadeLoads.set(cacheKey, ready);
  return ready;
}
